import { Action, ActionType } from 'game/quests/types';
import './styles.css';
import '../MissionStatusBadge/styles.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Store } from 'game/store/gameStore';
import { useDispatch } from 'react-redux';
import { closeMissionsModal, openDesignModal } from '../../../../../store/actions/gameUIActions';
import { DesignTab } from '../../../../../types/gameUI';
import { getQuestLabel } from 'game/quests/utils';
import { translate } from 'game/translate';
import { SolitaireGame } from 'game/SolitaireGame';

interface MissionActionProps {
    action: Action;
}   

export const MissionAction: React.FC<MissionActionProps> = ({ action }) => {
    const dispatch = useDispatch();
    const [isDisabledButton, setIsDisabledButton] = useState(false);
    const isCompleted = useMemo(() => action.passed, [action.passed]);
    const ref = useRef<HTMLDivElement>(null)
    const [isGhostProgressBarEnabled, setIsGhostProgressBarEnabled] = useState(true);
    const [baseHeight, setBaseHeight] = useState<number | 'auto'>(-1);


    useEffect(() => {
        if (baseHeight === -1 && ref.current) {
            setBaseHeight(ref.current?.clientHeight || 'auto');
            setIsGhostProgressBarEnabled(false);
        }
    }, []);

    const progressPercentage = useMemo(() => {
        return (action.progress / action.targetProgress) * 100;
    }, [action.progress, action.targetProgress]);

    const getProgressBar = useCallback((isGhost: boolean = false) => {
        if ((action.targetProgress > 1 || action.type == ActionType.WINS) && action.tag !== 'RESTRICTION') {
            return (
                <div style={{ marginTop: '6px', ...(isGhost && { opacity: 0 }) }} className="mission-progress-bar action-progress-bar">
                    <div 
                        className={`mission-progress-fill action-progress-bar-fill${isCompleted ? ' mission-progress-fill--filled' : ''}`}
                        style={{ width: `calc(${progressPercentage}% - 2px)` }}
                    />
                    <span className="mission-progress-text">
                        {Math.trunc(action.progress)}/{action.targetProgress}
                    </span>
                </div>
            )
        }
    }, [action.progress, action.targetProgress]);

    const handleCustomButtonClick = (buttonKey: string, data?: any) => {
        document.dispatchEvent(new CustomEvent('ui-button-click', {
          detail: { buttonKey, data }
        }));
    };

    const handleButtonClick = useCallback(() => {
        if (isDisabledButton) return;

        if (action.button?.buttonActionType === 'watch_ad') {
            handleCustomButtonClick('mission-ad');
        }

        if (action.button?.buttonActionType === 'design') {
            setTimeout(() => {
                dispatch(closeMissionsModal());
                dispatch(openDesignModal(action.button?.payload as DesignTab));
            }, 100);
        }

        if (action.button?.buttonActionType === 'play') {
            const difficulty = action.button.payload || Store.difficulty;

            if (action.button.dataDraw || action.button.dataSuit) {
                if (action.button.dataDraw) {
                    document.getElementById('game-container')?.setAttribute('data-draw', action.button.dataDraw);
                }

                if (action.button.dataSuit) {
                    document.getElementById('game-container')?.setAttribute('data-suit', action.button.dataSuit);
                }

                const typeEl = document.querySelector(".game-type.active");
                typeEl?.classList.remove("active");
            }
            document.querySelector('.carousel-item.active')?.classList.remove('active');

            Store.reInit();

            SolitaireGame.instance?.resetGame();
            
            if (difficulty) {
                setTimeout(() => {
                    dispatchEvent(
                        new CustomEvent("newGame", {
                            detail: {
                                difficulty,
                            },
                        })
                    );
                }, 50);

                setIsDisabledButton(true);
                setTimeout(() => {
                    setIsDisabledButton(false);
                }, 1300);
            }
        }
    }, [action.button, isDisabledButton]);

    const actionText = useMemo(() => getQuestLabel(action.label, action.targetProgress, action.targetProgress.toString()), [action]);

    return (
        <div style={{ height: baseHeight }} ref={ref} className={`mission-action${isCompleted ? ' mission-action--completed' : ''}`}>
            <div className="mission-action-content">
                <span className="mission-action-title">
                    {actionText}
                </span>

                {!isCompleted && !isGhostProgressBarEnabled && getProgressBar()}
                {isGhostProgressBarEnabled && getProgressBar(true)}
            </div>
            {!isCompleted && !action.button && <div className="mission-action-complete-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="16" viewBox="0 0 21 16" fill="none">
                    <path d="M18.1907 1.10936C17.8588 0.773858 17.3168 0.773856 16.9848 1.10935L9.1833 8.99304C8.8513 9.32853 8.3093 9.32853 7.97731 8.99302L4.35372 5.33112C4.02173 4.99561 3.47973 4.99561 3.14773 5.33111L1.09046 7.41009C0.763358 7.74064 0.763355 8.27292 1.09045 8.60348L5.32957 12.8874L7.99853 15.5846C8.33053 15.9201 8.87253 15.9201 9.20452 15.5846L11.8523 12.9089L20.2693 4.40323C20.5964 4.07267 20.5964 3.54039 20.2693 3.20983L18.1907 1.10936Z" fill="#1F1F1F"/>
                </svg>
            </div>}

            {isCompleted && <div className="mission-action-complete-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                    <path d="M20.1907 5.10936C19.8588 4.77386 19.3168 4.77386 18.9848 5.10935L11.1833 12.993C10.8513 13.3285 10.3093 13.3285 9.97731 12.993L6.35372 9.33112C6.02173 8.99561 5.47973 8.99561 5.14773 9.33111L3.09046 11.4101C2.76336 11.7406 2.76335 12.2729 3.09045 12.6035L7.32957 16.8874L9.99853 19.5846C10.3305 19.9201 10.8725 19.9201 11.2045 19.5846L13.8523 16.9089L22.2693 8.40323C22.5964 8.07267 22.5964 7.54039 22.2693 7.20983L20.1907 5.10936Z" fill="white"/>
                </svg>
            </div>}

            {action.button && !isCompleted && <div id={action.button.buttonActionType === 'play' ? 'force-close-panels' : ''} onClick={handleButtonClick} className={`mission-action-button${isDisabledButton ? ' mission-action-button--disabled' : ''}`}> 
                <div id={action.button.buttonActionType === 'play' ? 'force-close-panels' : ''} className="mission-action-button-text">
                {action.button.buttonActionType === 'watch_ad' && <svg style={{ marginRight: '4px' }} xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M18 8.27273H2V15.4245C2 16.5708 2.89861 17.5 4.0071 17.5H15.9929C17.1014 17.5 18 16.5708 18 15.4245V8.27273ZM9.26453 9.62126C9.06218 9.4603 8.76923 9.60947 8.76923 9.87346V15.2231C8.76923 15.4939 9.0757 15.6409 9.27567 15.466L12.4782 12.6638C12.6279 12.5328 12.6225 12.2923 12.467 12.1687L9.26453 9.62126Z" fill="white"/>
                    <path d="M2 5.57552C2 4.42924 2.89861 3.5 4.0071 3.5H8.10705L5.07692 7H2V5.57552Z" fill="white"/>
                    <path d="M6.66218 7L9.69231 3.5H13.6455L10.6154 7H6.66218Z" fill="white"/>
                    <path d="M12.2006 7H18V5.57552C18 4.42924 17.1014 3.5 15.9929 3.5H15.2308L12.2006 7Z" fill="white"/>
                </svg>}

                {action.button.buttonActionType === 'design' && <svg style={{ marginRight: '4px', marginTop: '1px' }} xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                        <path d="M10 18.5C8.90667 18.5 7.87333 18.2899 6.9 17.8696C5.92667 17.4493 5.07653 16.876 4.3496 16.1496C3.6232 15.4232 3.04987 14.5733 2.6296 13.6C2.20933 12.6267 1.99947 11.5933 2 10.5C2 9.39333 2.2168 8.35333 2.6504 7.38C3.084 6.40667 3.67067 5.56 4.4104 4.84C5.15013 4.12 6.01333 3.55013 7 3.1304C7.98667 2.71067 9.04 2.50053 10.16 2.5C11.2267 2.5 12.2333 2.68347 13.18 3.0504C14.1267 3.41733 14.9568 3.924 15.6704 4.5704C16.3835 5.2168 16.9501 5.98347 17.3704 6.8704C17.7907 7.75733 18.0005 8.71387 18 9.74C18 11.2733 17.5333 12.4501 16.6 13.2704C15.6667 14.0907 14.5333 14.5005 13.2 14.5H11.72C11.6 14.5 11.5165 14.5333 11.4696 14.6C11.4227 14.6667 11.3995 14.74 11.4 14.82C11.4 14.98 11.5 15.2101 11.7 15.5104C11.9 15.8107 12 16.1539 12 16.54C12 17.2067 11.8165 17.7 11.4496 18.02C11.0827 18.34 10.5995 18.5 10 18.5ZM5.6 11.3C5.94667 11.3 6.23333 11.1867 6.46 10.96C6.68667 10.7333 6.8 10.4467 6.8 10.1C6.8 9.75333 6.68667 9.46667 6.46 9.24C6.23333 9.01333 5.94667 8.9 5.6 8.9C5.25333 8.9 4.96667 9.01333 4.74 9.24C4.51333 9.46667 4.4 9.75333 4.4 10.1C4.4 10.4467 4.51333 10.7333 4.74 10.96C4.96667 11.1867 5.25333 11.3 5.6 11.3ZM8 8.1C8.34667 8.1 8.63333 7.98667 8.86 7.76C9.08667 7.53333 9.2 7.24667 9.2 6.9C9.2 6.55333 9.08667 6.26667 8.86 6.04C8.63333 5.81333 8.34667 5.7 8 5.7C7.65333 5.7 7.36667 5.81333 7.14 6.04C6.91333 6.26667 6.8 6.55333 6.8 6.9C6.8 7.24667 6.91333 7.53333 7.14 7.76C7.36667 7.98667 7.65333 8.1 8 8.1ZM12 8.1C12.3467 8.1 12.6333 7.98667 12.86 7.76C13.0867 7.53333 13.2 7.24667 13.2 6.9C13.2 6.55333 13.0867 6.26667 12.86 6.04C12.6333 5.81333 12.3467 5.7 12 5.7C11.6533 5.7 11.3667 5.81333 11.14 6.04C10.9133 6.26667 10.8 6.55333 10.8 6.9C10.8 7.24667 10.9133 7.53333 11.14 7.76C11.3667 7.98667 11.6533 8.1 12 8.1ZM14.4 11.3C14.7467 11.3 15.0333 11.1867 15.26 10.96C15.4867 10.7333 15.6 10.4467 15.6 10.1C15.6 9.75333 15.4867 9.46667 15.26 9.24C15.0333 9.01333 14.7467 8.9 14.4 8.9C14.0533 8.9 13.7667 9.01333 13.54 9.24C13.3133 9.46667 13.2 9.75333 13.2 10.1C13.2 10.4467 13.3133 10.7333 13.54 10.96C13.7667 11.1867 14.0533 11.3 14.4 11.3Z" fill="white"/>
                    </svg>
                </svg>}
                    {translate(action.button.text)}
                </div>
            </div>}
        </div>
    );
}